import _ from "lodash";
import { useContext, useState } from 'react';
import SingleTextEditor from "../../../components/common/editors/SingleTextEditor";
import MultiButtonSwitch from '../../../components/common/editors/MultiButtonSwitch';
import { Icon } from '../../../components/common/Icon';
import LegoAdminPageContext from '../../legoAdminPageContext';
import useAsyncEffect from '../../../components/common/react-hooks/useAsyncEffect';
import FragmentsUsingSpec from "./FragmentsUsingSpec";
import SpecGroupBadge from "./SpecGroupBadge";

const filterOptions = [
  { label: <Icon icon="code_off" level="danger" title='Invalid schema path or value' />, value: 'invalidPath' },
  { label: <Icon icon="flaky" level="danger" />, value: 'contradiction' },
  { label: <Icon icon="flaky" level="info" />, value: 'noConsensus' },
  { label: <Icon icon="search_off" level="warning" />, value: 'missingSpec' },
  { label: <Icon icon="gpp_bad" level="danger" title='Required value missing' />, value: 'requiredMissing' },
  { label: <Icon icon="functions" level="danger" title='Rule execution errors' />, value: 'ruleError' },
  { label: <Icon icon="functions" level="info" title='Rule generated values' />, value: 'ruleGenerated' },
];

export default function SpecsNoticesTable() {
  const modelsByCount = obj => _.sortBy(_.toPairs(obj.byModel), ([_, groupIds]) => -groupIds.length);

  const { page } = useContext(LegoAdminPageContext);
  const specsNoticeSvc = page.service('services/cerokm/specsnotice');

  const [filterType, setFilterType] = useState('requiredMissing');
  const [rows, setRows] = useState('');
  const [search, setSearch] = useState('');
  const [modelFilter, setModelFilter] = useState('');

  const hackedType = filterType === 'invalidPath' ? ['invalidPath', 'invalidValue'] : filterType;

  useAsyncEffect(async () => {
    page.runAsync(async () => {
      const countsByPath = await specsNoticeSvc.find({ query: { type: hackedType , countByPath: true } });
      const rows = _.sortBy(_.toPairs(countsByPath), ([path, obj]) => -obj.count);
      setRows(rows);
    });
  }, [filterType]);

  const onModelClick = (path, modelId, groupIds) => {
    page.openModal(<FragmentsUsingSpec path={path} specsGroupId={{$in: groupIds}} />, { title: <div className="py-2">
      Fragments with '{path}' in <SpecGroupBadge id={groupIds[0]} />
    </div>, fullScreen: true });
  }

  if (rows) {
    let filteredRows = rows.filter(row => !search || row[0].toLowerCase().includes(search.toLowerCase()));
    filteredRows = filteredRows.filter(row => !modelFilter || _.some(modelsByCount(row[1]), ([modelId, groupIds]) => modelId.toLowerCase().includes(modelFilter.toLowerCase())));

    return <table className="table table-sm">
      <thead className='bg-light'>
        <tr>
          <th colSpan={4}>
            <div className='d-flex align-items-center'>
              <MultiButtonSwitch options={filterOptions} value={filterType} onChange={setFilterType} />
              <span className='ml-3'>{`Selected type: ${filterType === 'invalidPath' ? 'invalidPath or invalidValue' : filterType}`}</span>
            </div>
          </th>
        </tr>
        <tr>
          <th className="align-middle">#</th>
          <th>
            <div className="d-flex align-items-center">
              Path <SingleTextEditor placeholder="Filter paths..." className='ml-2' small value={search} onChange={(value) => setSearch(value)} />
            </div>
          </th>
          <th className="align-middle">Count</th>
          <th>
            <div className="d-flex align-items-center">
              ModelIds 
              <SingleTextEditor placeholder="Filter modelIds..." className='ml-2 w-50' small value={modelFilter} onChange={(value) => setModelFilter(value)} />              
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr key="total" className='bg-light-info'>
          <td />
          <td className="font-weight-bold">TOTAL</td>
          <td className="font-weight-bold text-info">{_.sum(filteredRows.map(([path, obj]) => obj.count))}</td>
          <td />
        </tr>
        {filteredRows.map(([path, obj], index) => (
          <tr key={index}>
            <td className="text-secondary translucent">{_.findIndex(rows, (row) => row[0] == path) + 1}</td>
            <td>
              {filterType === 'requiredMissing' ? <a href={`/cerokm/add-missing-specs?specPath="${path}"`} target='_blank'>
                {path}
              </a> : path}
            </td>
            <td className="text-secondary">
              {obj.count}
            </td>
            <td>
              {_.map(modelsByCount(obj), ([modelId, groupIds]) => <span key={modelId} className="d-inline-block">
                <span className="mr-2 cursor-pointer" onClick={() => onModelClick(path, modelId, groupIds)}>
                  <span className="badge badge-secondary mb-1">
                    <span className="badge badge-pill badge-light text-primary mr-1">{groupIds.length}</span>
                    {modelId}
                  </span>
                </span>
              </span>)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  } else {
    return <div className={'p-3'}>Loading...</div>;
  }
}
